import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapTrifold, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { STAGE_LABELS, STAGE_COLORS } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Regions() {
  const [groups, setGroups] = useState(null);
  const [managers, setManagers] = useState({});

  const load = () => api.get("/branches/meta/regions").then((r) => {
    setGroups(r.data);
    setManagers(Object.fromEntries(r.data.map((g) => [g.region, g.manager || ""])));
  }).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const saveManagers = async () => {
    try {
      await api.put("/settings/regions", { managers });
      toast.success("Relationship managers saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!groups) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading regions…</div>;

  return (
    <div className="space-y-6" data-testid="regions-page">
      <PageHeader
        overline="Bank Branches" title="Regions / Zones"
        subtitle="Territory-based business development — assign a relationship manager per region"
        action={<Button onClick={saveManagers} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-managers-btn"><FloppyDisk className="mr-1" size={16} /> Save Managers</Button>}
      />

      {groups.length === 0 ? (
        <EmptyState icon={MapTrifold}>No branches yet. Add a region on each branch record to build your territory map.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {groups.map((g) => (
            <Card key={g.region} className="p-5 border-border" data-testid={`region-${g.region}`}>
              <div className="flex items-center gap-2">
                <MapTrifold size={18} weight="duotone" className="text-accent" />
                <h3 className="font-display font-semibold text-lg text-primary">{g.region}</h3>
                <span className="ml-auto text-xs text-muted-foreground">{g.branches.length} branches</span>
              </div>
              <div className="mt-3">
                <label className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground">Relationship Manager</label>
                <Input
                  className="mt-1 h-9"
                  value={managers[g.region] ?? ""}
                  onChange={(e) => setManagers({ ...managers, [g.region]: e.target.value })}
                  placeholder="Who owns this territory?"
                  data-testid={`region-manager-${g.region}`}
                />
              </div>
              <div className="mt-4 space-y-1.5">
                {g.branches.map((b) => (
                  <Link key={b.id} to={`/app/branches/${b.id}`} className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted hover:bg-muted/60 transition-colors duration-150" data-testid={`region-branch-${b.id}`}>
                    <span className="text-sm text-primary truncate">{b.bank_name} — {b.branch_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded border shrink-0 ${STAGE_COLORS[b.stage]}`}>{STAGE_LABELS[b.stage]}</span>
                  </Link>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
